import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import {
  useGetWorkflowsByAgency,
  useGetStepsByManyWorkflowIds,
} from '@frontend/features/workflows/queries';
import {
  MetaCapiConfigFormValues,
  useMetaCapiConfig,
  useUpdateMetaCapiConfig,
} from '@frontend/features/meta-capi/queries';

const DEFAULT_VALUES: MetaCapiConfigFormValues = {
  enabled: false,
  accessToken: '',
  datasetId: '',
  whatsappBusinessAccountId: '',
  graphApiVersion: '',
  testEventCode: '',
  defaultCurrency: '',
  purchaseTriggerType: '',
  purchaseTriggerStepId: '',
  purchaseTriggerFieldName: '',
  purchaseTriggerFieldValue: '',
};

export const MetaCapiSettingsView = () => {
  const { id } = useParams();
  const agencyId = Number(id);
  const { t } = useTranslation();
  const { addToast } = useSnackBar();

  const { data: config, isLoading } = useMetaCapiConfig(agencyId);
  const mutation = useUpdateMetaCapiConfig(agencyId);

  const { control, handleSubmit, reset, watch } = useForm<MetaCapiConfigFormValues>({
    defaultValues: DEFAULT_VALUES,
  });

  const purchaseTriggerType = watch('purchaseTriggerType');

  const { data: workflowsData } = useGetWorkflowsByAgency({
    agencyId,
    pageSize: 1000,
    currentPage: 1,
    search: '',
  });

  const workflowIds = useMemo(
    () => (workflowsData?.items || []).map((wf) => wf.id),
    [workflowsData],
  );

  const { data: stepsData } = useGetStepsByManyWorkflowIds({
    workflowIds,
    search: '',
  });

  const workflowNameById = useMemo(() => {
    const map = new Map<string, string>();
    (workflowsData?.items || []).forEach((wf) => map.set(wf.id, wf.name));
    return map;
  }, [workflowsData]);

  const stepOptions = useMemo(
    () =>
      (stepsData || []).map((step) => ({
        id: step.id,
        name: step.name,
        workflowName: workflowNameById.get(step.workflowId) || '',
      })),
    [stepsData, workflowNameById],
  );

  useEffect(() => {
    if (config) {
      reset({
        enabled: config.enabled,
        accessToken: '',
        datasetId: config.datasetId || '',
        whatsappBusinessAccountId: config.whatsappBusinessAccountId || '',
        graphApiVersion: config.graphApiVersion || '',
        testEventCode: config.testEventCode || '',
        defaultCurrency: config.defaultCurrency || '',
        purchaseTriggerType: config.purchaseTriggerType || '',
        purchaseTriggerStepId: config.purchaseTriggerStepId || '',
        purchaseTriggerFieldName: config.purchaseTriggerFieldName || '',
        purchaseTriggerFieldValue: config.purchaseTriggerFieldValue || '',
      });
    }
  }, [config, reset]);

  const onSubmit = async (values: MetaCapiConfigFormValues) => {
    try {
      await mutation.mutateAsync(values);
      addToast({ message: t('metaCapi.form.saveSuccess'), color: 'success' });
    } catch (e) {
      addToast({ message: t('metaCapi.form.saveError'), color: 'error' });
    }
  };

  return (
    <ListLayout padding={0} title={t('metaCapi.title')} subtitle={t('metaCapi.subtitle')}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap={2} maxWidth={640}>
          <Controller
            name="enabled"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t('metaCapi.form.enabled')}
              />
            )}
          />

          <Typography variant="subtitle2" color="text.secondary">
            {t('metaCapi.form.credentialsSection')}
          </Typography>

          <Controller
            name="accessToken"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="password"
                label={t('metaCapi.form.accessToken')}
                fullWidth
                helperText={
                  config?.accessTokenSet
                    ? t('metaCapi.form.accessTokenEditHelper')
                    : t('metaCapi.form.accessTokenHelper')
                }
              />
            )}
          />

          <Controller
            name="datasetId"
            control={control}
            render={({ field }) => (
              <TextField {...field} label={t('metaCapi.form.datasetId')} fullWidth />
            )}
          />

          <Controller
            name="whatsappBusinessAccountId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('metaCapi.form.whatsappBusinessAccountId')}
                fullWidth
              />
            )}
          />

          <Controller
            name="graphApiVersion"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('metaCapi.form.graphApiVersion')}
                placeholder="v21.0"
                fullWidth
                helperText={t('metaCapi.form.graphApiVersionHelper')}
              />
            )}
          />

          <Controller
            name="defaultCurrency"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('metaCapi.form.defaultCurrency')}
                placeholder="USD"
                fullWidth
              />
            )}
          />

          <Controller
            name="testEventCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('metaCapi.form.testEventCode')}
                fullWidth
                helperText={t('metaCapi.form.testEventCodeHelper')}
              />
            )}
          />

          <Divider />

          <Typography variant="subtitle2" color="text.secondary">
            {t('metaCapi.form.purchaseTriggerSection')}
          </Typography>

          <Controller
            name="purchaseTriggerType"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label={t('metaCapi.form.purchaseTriggerType')}
                fullWidth
                helperText={t('metaCapi.form.purchaseTriggerTypeHelper')}
              >
                <MenuItem value="">{t('metaCapi.form.purchaseTriggerNone')}</MenuItem>
                <MenuItem value="step">{t('metaCapi.form.purchaseTriggerStep')}</MenuItem>
                <MenuItem value="custom_field">
                  {t('metaCapi.form.purchaseTriggerCustomField')}
                </MenuItem>
              </TextField>
            )}
          />

          {purchaseTriggerType === 'step' && (
            <Controller
              name="purchaseTriggerStepId"
              control={control}
              render={({ field }) => {
                const selectedOption =
                  stepOptions.find((opt) => opt.id === field.value) || null;
                return (
                  <Autocomplete
                    options={stepOptions}
                    value={selectedOption}
                    onChange={(_, newValue) => field.onChange(newValue?.id || '')}
                    getOptionLabel={(option) =>
                      option.workflowName
                        ? `${option.name} (${option.workflowName})`
                        : option.name
                    }
                    groupBy={(option) => option.workflowName}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('metaCapi.form.purchaseTriggerStep')}
                        placeholder={t('metaCapi.form.purchaseTriggerStepPlaceholder')}
                      />
                    )}
                  />
                );
              }}
            />
          )}

          {purchaseTriggerType === 'custom_field' && (
            <>
              <Controller
                name="purchaseTriggerFieldName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('metaCapi.form.purchaseTriggerFieldName')}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="purchaseTriggerFieldValue"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('metaCapi.form.purchaseTriggerFieldValue')}
                    fullWidth
                    helperText={t('metaCapi.form.purchaseTriggerFieldValueHelper')}
                  />
                )}
              />
            </>
          )}

          <Box>
            <Button
              variant="contained"
              type="submit"
              disabled={mutation.isLoading || isLoading}
            >
              {t('metaCapi.form.save')}
            </Button>
          </Box>
        </Box>
      </form>
    </ListLayout>
  );
};

export default MetaCapiSettingsView;
